import React, { useEffect, useRef, useState } from 'react';

import { Card, message } from 'antd';
import { models } from 'powerbi-client';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { setReportPassword } from 'redux/actions/reportActions/Actions';
import { ReportState } from 'redux/reducers/ReportReducer';

import Layout from 'components/Layout/Layout';

import { ApiErrorData } from 'models/ApiResponse';
import { MenuNode } from 'models/MenuNode';
import { PowerBiAdditionalConfig } from 'models/PowerBiAdditionalConfig';
import { PowerBiReportData } from 'models/PowerBiReportData';
import { User } from 'models/User';

import { MenuService } from 'services/MenuService';
import { PowerBiReportService } from 'services/PowerBiReportService';

import { MenuType } from 'config/Config';
import { useThemeLayoutConfig } from 'config/ThemeDashboardLayoutConfig';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import FullscreenButton from './components/FullscreenButton';
import PasswordOverlay from './components/PasswordOverlay';
import ReportContent from './components/ReportContent';

import './PowerBiReport.scss';

const PASSWORD_SESSION_DURATION = 30 * 60 * 1000; // 30 minutos en ms

function PowerBiReport() {
  const { i18n, t } = useTranslation();
  const { theme } = useThemeLayoutConfig();
  const { key }: any = useParams();
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();
  const user: User = useSelector((state: any) => state.auth);
  const { passwords }: ReportState = useSelector((state: any) => state.report);
  const [title, setTitle] = useState('');

  const [embedConfig, setEmbedConfig] = useState<any>(null);
  const powerBIContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const [validatingPassword, setValidatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const reportKey = typeof key === 'string' ? key : '';
  const storedData = reportKey ? passwords[reportKey] : undefined;
  const isAuthenticated =
    !!storedData &&
    Date.now() - storedData.timestamp < PASSWORD_SESSION_DURATION;

  // Derivamos los datos del menú de forma síncrona para evitar parpadeos (flicker)
  const menuData = React.useMemo(() => {
    return FunctionsHelper.buildBreadcrumbForCode(
      (user?.menus as MenuNode[]) || [],
      key,
      {
        allowedTypes: [MenuType.POWER_BI_REPORT],
        includeAllParents: false,
        includeHome: false,
        showLeafIcon: true,
      }
    );
  }, [key, user?.menus]);

  const availableData = menuData
    ? menuData.chain[menuData.chain.length - 1]
    : null;
  const isEncrypted = !!availableData?.additionalConfig?.isPasswordProtected;

  // El overlay solo se muestra si el reporte requiere contraseña Y el usuario NO está autenticado
  const showPasswordOverlay = isEncrypted && !isAuthenticated;

  const loadReportData = async (additionalConfig: PowerBiAdditionalConfig) => {
    const reportDataResponse = await PowerBiReportService.getFullDataForReport(
      additionalConfig
    );

    if (reportDataResponse.success) {
      const { embedToken, embedUrl, id } =
        reportDataResponse.data as PowerBiReportData;

      setEmbedConfig({
        type: 'report',
        id,
        embedUrl,
        accessToken: embedToken,
        tokenType: models.TokenType.Embed,
        settings: {
          panes: {
            filters: { visible: !!additionalConfig.settings?.showFilterPane },
            pageNavigation: {
              visible: !!additionalConfig.settings?.showPageNavigation,
            },
          },
        },
      });
      setUseFallback(false);
    } else {
      const error = reportDataResponse.data as ApiErrorData;
      setEmbedConfig(null);

      const validatedFallback = validateHttpUrl(additionalConfig.fallbackUrl);
      if (validatedFallback) {
        setUseFallback(true);
        messageApi.warning(
          t('scenes.power_bi_report.messages.showing_fallback'),
          3.5
        );
      } else {
        setUseFallback(false);
        messageApi.error(
          (error.message as string) ||
            t('scenes.power_bi_report.messages.contact_executive'),
          3.5
        );
      }
    }
  };

  /* Settings */
  const heightOffset = 164 + 36;
  const vh = window.innerHeight;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullscreenChange
      );
    };
  }, []);

  // Reset completo de todos los estados cuando cambia el key del reporte
  useEffect(() => {
    setEmbedConfig(null);
    setUseFallback(false);
    setFallbackUrl(null);
    setValidatingPassword(false);
    setPasswordError(null);
    setTitle('');
  }, [key]);

  useEffect(() => {
    init();
  }, [key, i18n.language, isAuthenticated]);

  useEffect(() => {
    setPasswordError(null);
  }, [reportKey]);

  const validateHttpUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
        ? parsed.toString()
        : null;
    } catch (e) {
      return null;
    }
  };

  const init = async () => {
    if (availableData) {
      const additionalConfig: PowerBiAdditionalConfig =
        availableData.additionalConfig;

      const validatedFallback = validateHttpUrl(additionalConfig.fallbackUrl);
      setFallbackUrl(validatedFallback);

      if (isEncrypted && !isAuthenticated) {
        setTitle(menuData?.title || availableData.title);
        return;
      }

      if (!(additionalConfig.reportId && additionalConfig.groupId)) {
        console.error(
          'Error, no se encontró los datos adicionales requeridos',
          additionalConfig
        );
        messageApi.error(
          t('scenes.power_bi_report.messages.required_data_not_found'),
          3.5
        );
        if (validatedFallback) {
          setUseFallback(true);
          setEmbedConfig(null);
          return;
        }
      }

      setTitle(menuData?.title || availableData.title);

      await loadReportData(additionalConfig);
    } else {
      setTitle('');
      setEmbedConfig(null);
      setUseFallback(false);
      setFallbackUrl(null);
    }
  };

  const handlePasswordValidation = async (values: { password: string }) => {
    setValidatingPassword(true);
    setPasswordError(null);

    const response = await MenuService.validateEncryptedReportSecret(key, {
      secret: values.password,
    });

    if (
      response.success &&
      response.data &&
      'validated' in response.data &&
      (response.data as { validated: boolean }).validated
    ) {
      setPasswordError(null);
      dispatch(setReportPassword(reportKey, values.password));
      // No llamamos a init() ni loadReportData() aquí manualmente.
      // El dispatch actualizará isAuthenticated, lo que disparará el useEffect que llama a init().
    } else {
      setPasswordError(
        t('scenes.power_bi_report.password_modal.invalid_password_simple')
      );
    }

    setValidatingPassword(false);
  };

  const handleFullscreen = () => {
    const container = powerBIContainerRef.current;
    if (container) {
      if (!isFullscreen) {
        container.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  return (
    <>
      {contextHolder}
      <Layout title={title}>
        <div
          className="power-bi-report-wrapper"
          style={
            {
              '--report-primary-color': theme?.token?.colorPrimary,
            } as React.CSSProperties
          }
        >
          {showPasswordOverlay && (
            <PasswordOverlay
              key={key}
              validatingPassword={validatingPassword}
              passwordError={passwordError}
              onValidate={handlePasswordValidation}
            />
          )}

          <Card
            style={{
              width: '100%',
              height: isFullscreen ? '100vh' : `${vh - heightOffset}px`,
              position: 'relative',
            }}
            className="power-bi-card-container"
            loading={!embedConfig && !useFallback}
          >
            <div
              ref={powerBIContainerRef}
              style={{ width: '100%', height: '100%' }}
            >
              <ReportContent
                useFallback={useFallback}
                fallbackUrl={fallbackUrl}
                embedConfig={embedConfig}
                hasEncryption={isEncrypted}
                isAuthenticated={isAuthenticated}
                onEmbedError={() => {
                  if (fallbackUrl) {
                    setUseFallback(true);
                    setEmbedConfig(null);
                    messageApi.warning(
                      t(
                        'scenes.power_bi_report.messages.error_loading_fallback'
                      ),
                      3.5
                    );
                  } else {
                    messageApi.error(
                      t('scenes.power_bi_report.messages.error_loading_report'),
                      3.5
                    );
                  }
                }}
              />
            </div>

            {(embedConfig || (useFallback && fallbackUrl)) && (
              <FullscreenButton
                isFullscreen={isFullscreen}
                onToggle={handleFullscreen}
              />
            )}
          </Card>
        </div>
      </Layout>
    </>
  );
}

export default PowerBiReport;
